import React, { useState, useMemo } from 'react';
import { reportsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MultiSelect } from './MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, Download, Info, Users, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

// "We need number of students registered for lunch, club, uniform." Extended
// 2026-08-18 to transport and every other billable fee head.
//
// Nothing in the system records a sign-up: lunch_coupons and club_attendance are
// empty, and a uniform issue or transport route isn't tracked anywhere. Being
// BILLED for the head is the only evidence a student took it, so that is what this
// counts — stated plainly on screen so nobody reads these as attendance figures.
//
// The head list is never hard-coded here: it comes from /api/config
// (fee_head_display) and is widened by whatever the report itself found on
// invoices, so a head added in config.py needs no edit in this file.

// Fallback only — used for the very first render, before config or a response has
// arrived. Priority order, matching config.FEE_HEAD_PRIORITY.
const FALLBACK_HEADS = [
  'uniform', 'lunch', 'club', 'transport', 'examination', 'tuition', 'id_cards',
  'registration', 'capital_levy', 'day_care', 'field_trips', 'jersey', 'textbooks',
  'locker_keys', 'others',
];

// The opt-in extras this report was originally built for. Kept as a one-click
// preset now that the default is every head.
const SERVICE_HEADS = ['uniform', 'lunch', 'club', 'transport', 'day_care', 'field_trips'];

const headLabel = (code) =>
  String(code || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const usd = (n) =>
  `USD ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (n) => Number(n || 0).toLocaleString();

// Share of a head's billing that has come in. Guarded: heads can be billed zero
// (a fully discounted line), and a receipt can overshoot the bill.
const pct = (part, whole) => (Number(whole) > 0
  ? `${Math.round((Number(part || 0) / Number(whole)) * 100)}%`
  : '—');

// `csv` lets a column export a raw number where the screen shows "USD 1,234.00",
// so the export lands in Excel as something you can sum.
const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => columns.map((c) => esc((c.csv || c.get)(r))).join(','))].join('\n');
};

const downloadBlob = (name, payload, type) => {
  const blob = payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const STUDENT_COLUMNS = [
  { label: 'Student ID', get: (r) => r.student_code },
  { label: 'Student', get: (r) => r.student_name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Status', get: (r) => r.status },
  { label: 'Academic Year', get: (r) => r.academic_year },
  { label: 'Term', get: (r) => r.term },
  { label: 'Fee Head', get: (r) => r.fee_head_label },
  { label: 'Invoice #', get: (r) => (r.invoice_count > 1
    ? `${r.invoice_number} +${r.invoice_count - 1}` : r.invoice_number) },
  { label: 'Billed (USD)', get: (r) => usd(r.amount_usd), csv: (r) => r.amount_usd, align: 'right' },
  { label: 'Collected (USD)', get: (r) => usd(r.collected_usd), csv: (r) => r.collected_usd, align: 'right' },
  { label: 'Balance (USD)', get: (r) => usd(r.balance_usd), csv: (r) => r.balance_usd, align: 'right' },
];

// Named tones for the heads people look at most; anything else cycles the palette
// below by position, so a KPI card is never left unstyled however many heads show.
const HEAD_TONE = {
  lunch: 'bg-[#3E3F96]/10 border-[#3E3F96]/25',
  club: 'bg-[#5556A8]/10 border-[#5556A8]/25',
  uniform: 'bg-[#D99C3B]/10 border-[#D99C3B]/25',
  transport: 'bg-[#2E7D6B]/10 border-[#2E7D6B]/25',
};

const TONE_CYCLE = [
  'bg-[#7B7CC4]/10 border-[#7B7CC4]/25',
  'bg-[#C85A40]/10 border-[#C85A40]/25',
  'bg-[#4E8AB8]/10 border-[#4E8AB8]/25',
  'bg-[#8A9A5B]/10 border-[#8A9A5B]/25',
  'bg-[#B07AA1]/10 border-[#B07AA1]/25',
];

const headTone = (code, idx) =>
  HEAD_TONE[code] || TONE_CYCLE[idx % TONE_CYCLE.length];

const RegistrationsReport = ({ configBranches = [], configClasses = [], configFeeHeads = {} }) => {
  const { user, isAdmin, userBranches } = useAuth();
  const branchOptions = isAdmin ? configBranches : (userBranches || []);
  // Mirrors require_roles on the export endpoint.
  const canExport = ['admin', 'manager'].includes(user?.role);

  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [term, setTerm] = useState('all');
  const [fy, setFy] = useState('all');
  // Empty = every fee head, the same convention as every other filter in the app.
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // What the shown data was actually fetched with — drill-downs and the export
  // must use this, not dropdowns the user may have changed since Submit.
  const [applied, setApplied] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');
  // Class breakup: school-wide by default, or split per branch when they want
  // "class wise within each branch".
  const [classPerBranch, setClassPerBranch] = useState(false);

  const fyOptions = data?.available_fy?.length
    ? data.available_fy
    : ['2024-2025', '2025-2026', '2026-2027'].map((y) => ({ value: y, label: `${y.slice(2, 4)}-${y.slice(7, 9)}` }));

  // Heads offered in the picker: what the last response reported (config heads plus
  // anything found on invoices), else /api/config, else the local fallback. The
  // response wins because only it knows about heads that exist in data but not in
  // config — nothing here is ever narrowed to a fixed three.
  const headOptions = useMemo(() => {
    if (data?.fee_head_options?.length) return data.fee_head_options;
    const codes = Object.keys(configFeeHeads || {});
    return (codes.length ? codes : FALLBACK_HEADS)
      .map((c) => ({ value: c, label: configFeeHeads?.[c] || headLabel(c) }));
  }, [data, configFeeHeads]);

  const serviceHeads = useMemo(() => {
    const known = new Set(headOptions.map((o) => o.value));
    return (data?.service_fee_heads?.length ? data.service_fee_heads : SERVICE_HEADS)
      .filter((h) => known.has(h));
  }, [data, headOptions]);

  const buildParams = () => ({
    fy,
    term,
    ...(branches.length ? { branch: branches.join(',') } : {}),
    ...(classes.length ? { current_class: classes.join(',') } : {}),
    ...(heads.length ? { fee_heads: heads.join(',') } : {}),
  });

  const submit = async () => {
    // No "pick at least one" guard: an empty selection means every head, and the
    // backend defaults the same way.
    setLoading(true);
    const params = buildParams();
    try {
      const res = await reportsApi.registrations(params);
      setData(res.data);
      setApplied(params);
      setDrill(null);
      setDrillData(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load registrations report');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!applied) return;
    setExporting(true);
    try {
      const res = await reportsApi.exportRegistrationsExcel(applied);
      const tag = `${applied.fy}_${applied.term === 'all' ? 'all-terms' : applied.term.replace(/\s/g, '')}`;
      downloadBlob(`registrations_${tag}.xlsx`, res.data);
      toast.success('Excel exported');
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'Only Admin and Manager can export reports'
        : 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Drill into one FY x Term x Fee Head cell (or a whole fee head).
  const openDrill = async (cell) => {
    if (!applied) return;
    setDrill(cell);
    setDrillSearch('');
    setDrillData(null);
    setDrillLoading(true);
    try {
      const params = {
        ...applied,
        ...(cell.academic_year ? { fy: cell.academic_year } : {}),
        ...(cell.term ? { term: cell.term } : {}),
        ...(cell.fee_head ? { fee_heads: cell.fee_head } : {}),
        // A branch / class cell narrows to that branch / class; a row total keeps
        // whatever filters were applied at Submit.
        ...(cell.branch ? { branch: cell.branch } : {}),
        ...(cell.class_name ? { current_class: cell.class_name } : {}),
      };
      const res = await reportsApi.registrationsStudents(params);
      setDrillData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load students');
    } finally {
      setDrillLoading(false);
    }
  };

  const filteredDrillRows = useMemo(() => {
    const rows = drillData?.rows || [];
    const q = drillSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.student_name, r.student_code, r.branch, r.current_class, r.invoice_number]
        .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [drillData, drillSearch]);

  const rows = data?.rows || [];
  const totals = data?.totals;
  // Heads that were asked for but produced nothing. Named on screen so an all-heads
  // run reads as "checked and empty" rather than "quietly missing".
  const emptyHeads = useMemo(() => {
    const withData = new Set(data?.fee_heads_with_data || []);
    const label = (c) => headOptions.find((o) => o.value === c)?.label || headLabel(c);
    return (data?.fee_heads || []).filter((h) => !withData.has(h)).map(label);
  }, [data, headOptions]);
  const branchCols = data?.branch_columns || [];
  // Branch cells hold the count for that FY/Term/Fee-Head; a student belongs to one
  // branch only, so the branch columns add across to the row total.
  const cell = (r, b) => r.by_branch?.[b]?.students || 0;
  const classRows = (classPerBranch ? data?.by_branch_class : data?.by_class) || [];

  return (
    <div className="space-y-4">
      {/* ---- Filters ---- */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Branch</Label>
              <MultiSelect options={branchOptions} selected={branches} onChange={setBranches}
                placeholder="All Branches" testId="reg-branch" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Class</Label>
              <MultiSelect options={configClasses} selected={classes} onChange={setClasses}
                placeholder="All Classes" testId="reg-class" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Fee Head</Label>
              <div className="flex items-center gap-1">
                <MultiSelect options={headOptions} selected={heads} onChange={setHeads}
                  placeholder={`All Heads (${headOptions.length})`} testId="reg-head" />
                {/* One click back to the opt-in extras this report started as. */}
                <Button type="button" variant="outline" size="sm" className="text-xs px-2"
                  onClick={() => setHeads(
                    heads.length === serviceHeads.length
                      && serviceHeads.every((h) => heads.includes(h)) ? [] : serviceHeads)}
                  title="Lunch, club, uniform, transport, day care and field trips only"
                  data-testid="reg-head-services">
                  {heads.length === serviceHeads.length
                    && serviceHeads.every((h) => heads.includes(h)) ? 'All heads' : 'Services'}
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Term</Label>
              <Select value={term} onValueChange={setTerm}>
                <SelectTrigger className="w-40" data-testid="reg-term"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Terms</SelectItem>
                  {(data?.terms || ['Term 1', 'Term 2', 'Term 3']).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">FY</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger className="w-40" data-testid="reg-fy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All FY</SelectItem>
                  {fyOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={submit} disabled={loading} className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
              data-testid="reg-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card className="border-[#D8D9E8]">
          <CardContent className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
            <Users className="w-12 h-12 mb-4 opacity-50" />
            <p>Pick a branch, fee head, term and FY, then hit Submit.</p>
            <p className="text-xs mt-1">Leave Fee Head empty for every head, or hit Services for
              the opt-in extras only.</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* ---- What this counts. Stated up front so these are never read as
                  attendance or kitchen numbers. ---- */}
          <div className="flex items-start gap-2 rounded-lg border border-[#D8D9E8] bg-[#F5F5FA] p-3 text-xs text-[#5C635F]"
            data-testid="reg-basis-note">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#3E3F96]" />
            <span>
              A student counts as registered for a fee head in a term when they were
              <strong> billed</strong> for it — at least one invoice or invoice line for that
              head in that term. The system keeps no separate sign-up record for any head — no
              lunch coupon, club roll, uniform issue or transport route — so billing is the only
              registration signal available. Counting is distinct per student per term.
              {' '}Heads billed to everyone (tuition especially) are included for completeness —
              read those as billed, not as an opt-in.
              {emptyHeads.length > 0 && (
                <>
                  {' '}Nothing was billed in this selection for: <strong>{emptyHeads.join(', ')}</strong>,
                  so those heads have no row below.
                </>
              )}
            </span>
          </div>

          {/* ---- How the money columns are worked out. Spelled out because the
                  head-wise split of a receipt is an apportionment, not a record. ---- */}
          <div className="flex items-start gap-2 rounded-lg border border-[#D8D9E8] bg-[#F5F5FA] p-3 text-xs text-[#5C635F]"
            data-testid="reg-money-note">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#1A4331]" />
            <span>
              <strong>Collected</strong> is money actually received against those invoices.
              A receipt is recorded against an invoice, never against one of its lines, so on a
              multi-head invoice it is split across the heads in proportion to what each was
              billed; on a single-head invoice it is exact. <strong>Balance</strong> is the
              invoice's own outstanding figure, split the same way — so where a credit note or
              waiver cut a bill, Billed minus Collected comes out larger than Balance.
            </span>
          </div>

          {/* ---- Per-head totals across the whole selection ---- */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(data.by_fee_head || []).map((h, i) => (
              <div key={h.fee_head}
                className={`p-3 rounded-lg border ${headTone(h.fee_head, i)}`}
                data-testid={`reg-kpi-${h.fee_head}`}>
                <p className="text-xs text-[#5C635F] uppercase font-semibold truncate"
                  title={h.fee_head_label}>{h.fee_head_label}</p>
                <p className="text-2xl font-bold tabular-nums mt-1">{num(h.students)}</p>
                <p className="text-xs text-[#5C635F] mt-1">
                  distinct students · {usd(h.amount_usd)} billed
                </p>
                <p className="text-xs text-[#5C635F] mt-0.5">
                  <span className="font-semibold text-[#1A4331]">{usd(h.collected_usd)}</span> collected
                  {' '}({pct(h.collected_usd, h.amount_usd)}) · {usd(h.balance_usd)} due
                </p>
              </div>
            ))}
            <div className="p-4 rounded-lg border bg-[#ECEDF5] border-[#D8D9E8]" data-testid="reg-kpi-distinct">
              <p className="text-xs text-[#5C635F] uppercase font-semibold">Distinct Students</p>
              <p className="text-2xl font-bold tabular-nums mt-1">{num(totals?.distinct_students)}</p>
              <p className="text-xs text-[#5C635F] mt-1">
                unique students across all heads shown
              </p>
            </div>
          </div>

          {/* ---- Branch-wise breakup ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Branch-wise breakup</CardTitle>
              <Button variant="outline" size="sm"
                onClick={() => downloadBlob('registrations_by_branch.csv',
                  toCsv(data.by_branch || [], [
                    { label: 'Branch', get: (b) => b.branch },
                    ...(data.by_fee_head || []).map((h) => ({
                      label: h.fee_head_label,
                      get: (b) => b.heads?.[h.fee_head]?.students || 0,
                    })),
                    { label: 'Registrations', get: (b) => b.registrations },
                    { label: 'Distinct Students', get: (b) => b.students },
                    { label: 'Billed (USD)', get: (b) => b.amount_usd },
                    { label: 'Collected (USD)', get: (b) => b.collected_usd },
                    { label: 'Balance (USD)', get: (b) => b.balance_usd },
                  ]), 'text/csv;charset=utf-8;')}
                data-testid="reg-branch-csv">
                <Download className="w-4 h-4 mr-1" />CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Branch</TableHead>
                      {(data.by_fee_head || []).map((h) => (
                        <TableHead key={h.fee_head}
                          className="text-xs uppercase font-semibold text-[#5C635F] text-right whitespace-nowrap">
                          {h.fee_head_label}
                        </TableHead>
                      ))}
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right border-l border-[#D8D9E8]">
                        Registrations
                      </TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">
                        Distinct Students
                      </TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Billed (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Collected (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Balance (USD)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_branch || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6 + (data.by_fee_head || []).length}
                          className="text-center text-[#5C635F] py-8">
                          Nothing billed for these heads in this selection.
                        </TableCell>
                      </TableRow>
                    ) : (data.by_branch || []).map((b) => (
                      <TableRow key={b.branch} className="hover:bg-[#F5F5FA]"
                        data-testid={`reg-branch-row-${b.branch}`}>
                        <TableCell className="font-medium">{b.branch}</TableCell>
                        {(data.by_fee_head || []).map((h) => (
                          <TableCell key={h.fee_head} className="text-right tabular-nums p-0">
                            {b.heads?.[h.fee_head]?.students ? (
                              <button type="button"
                                className="w-full h-full px-4 py-2 text-right text-[#3E3F96] hover:underline"
                                onClick={() => openDrill({
                                  branch: b.branch,
                                  fee_head: h.fee_head,
                                  fee_head_label: h.fee_head_label,
                                  fy_label: applied?.fy === 'all' ? 'all FY' : applied?.fy,
                                  term: applied?.term === 'all' ? '' : applied?.term,
                                })}
                                data-testid={`reg-branch-cell-${b.branch}-${h.fee_head}`}
                              >
                                {num(b.heads[h.fee_head].students)}
                              </button>
                            ) : (
                              <span className="block px-4 py-2 text-[#B7BAC6]">0</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums font-bold text-[#3E3F96] border-l border-[#D8D9E8]">
                          {num(b.registrations)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(b.students)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(b.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331] font-medium">
                          {usd(b.collected_usd)}
                          <span className="ml-1 text-[10px] text-[#5C635F]">{pct(b.collected_usd, b.amount_usd)}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(b.balance_usd)}</TableCell>
                      </TableRow>
                    ))}
                    {(data.by_branch || []).length > 0 && (
                      <TableRow className="bg-[#ECEDF5] font-bold border-t-2 border-[#3E3F96]"
                        data-testid="reg-branch-total-row">
                        <TableCell className="uppercase text-xs tracking-wide">Total count</TableCell>
                        {(data.by_fee_head || []).map((h) => (
                          <TableCell key={h.fee_head} className="text-right tabular-nums">
                            {num((data.by_branch || []).reduce(
                              (s, b) => s + (b.heads?.[h.fee_head]?.students || 0), 0))}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums text-[#3E3F96] border-l border-[#D8D9E8]">
                          {num(totals?.students)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(totals?.distinct_students)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals?.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331]">
                          {usd(totals?.collected_usd)}
                          <span className="ml-1 text-[10px] font-normal text-[#5C635F]">
                            {pct(totals?.collected_usd, totals?.amount_usd)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(totals?.balance_usd)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {(data.by_branch || []).length > 0 && (
                <p className="px-4 py-3 text-xs text-[#5C635F] border-t border-[#D8D9E8]">
                  Fee-head columns count each student once per branch across all terms shown, so
                  they do not add up to Registrations (which counts a student once per term).
                  Distinct Students counts each person once, whatever they registered for.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ---- Class-wise breakup ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">
                Class-wise breakup
                {classPerBranch && <span className="ml-2 text-sm font-normal text-[#5C635F]">per branch</span>}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant={classPerBranch ? 'default' : 'outline'} size="sm"
                  className={classPerBranch ? 'bg-[#3E3F96] hover:bg-[#2D2E7A]' : ''}
                  onClick={() => setClassPerBranch((v) => !v)}
                  data-testid="reg-class-split-toggle"
                >
                  Split by branch
                </Button>
                <Button variant="outline" size="sm"
                  onClick={() => downloadBlob(
                    classPerBranch ? 'registrations_by_branch_class.csv' : 'registrations_by_class.csv',
                    toCsv(classRows, [
                      ...(classPerBranch ? [{ label: 'Branch', get: (c) => c.branch }] : []),
                      { label: 'Class', get: (c) => c.class_name },
                      ...(data.by_fee_head || []).map((h) => ({
                        label: h.fee_head_label,
                        get: (c) => c.heads?.[h.fee_head]?.students || 0,
                      })),
                      { label: 'Registrations', get: (c) => c.registrations },
                      { label: 'Distinct Students', get: (c) => c.students },
                      { label: 'Billed (USD)', get: (c) => c.amount_usd },
                      { label: 'Collected (USD)', get: (c) => c.collected_usd },
                      { label: 'Balance (USD)', get: (c) => c.balance_usd },
                    ]), 'text/csv;charset=utf-8;')}
                  data-testid="reg-class-csv">
                  <Download className="w-4 h-4 mr-1" />CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[32rem]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      {classPerBranch && (
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Branch</TableHead>
                      )}
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Class</TableHead>
                      {(data.by_fee_head || []).map((h) => (
                        <TableHead key={h.fee_head}
                          className="text-xs uppercase font-semibold text-[#5C635F] text-right whitespace-nowrap">
                          {h.fee_head_label}
                        </TableHead>
                      ))}
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right border-l border-[#D8D9E8]">
                        Registrations
                      </TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">
                        Distinct Students
                      </TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Billed (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Collected (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Balance (USD)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6 + (classPerBranch ? 1 : 0) + (data.by_fee_head || []).length}
                          className="text-center text-[#5C635F] py-8">
                          Nothing billed for these heads in this selection.
                        </TableCell>
                      </TableRow>
                    ) : classRows.map((c) => (
                      <TableRow key={`${c.branch || ''}-${c.class_name}`} className="hover:bg-[#F5F5FA]"
                        data-testid={`reg-class-row-${c.branch || 'all'}-${c.class_name}`}>
                        {classPerBranch && <TableCell className="text-[#5C635F]">{c.branch}</TableCell>}
                        <TableCell className="font-medium">{c.class_name}</TableCell>
                        {(data.by_fee_head || []).map((h) => (
                          <TableCell key={h.fee_head} className="text-right tabular-nums p-0">
                            {c.heads?.[h.fee_head]?.students ? (
                              <button type="button"
                                className="w-full h-full px-4 py-2 text-right text-[#3E3F96] hover:underline"
                                onClick={() => openDrill({
                                  class_name: c.class_name,
                                  ...(c.branch ? { branch: c.branch } : {}),
                                  fee_head: h.fee_head,
                                  fee_head_label: h.fee_head_label,
                                  fy_label: applied?.fy === 'all' ? 'all FY' : applied?.fy,
                                  term: applied?.term === 'all' ? '' : applied?.term,
                                })}
                                data-testid={`reg-class-cell-${c.class_name}-${h.fee_head}`}
                              >
                                {num(c.heads[h.fee_head].students)}
                              </button>
                            ) : (
                              <span className="block px-4 py-2 text-[#B7BAC6]">0</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums font-bold text-[#3E3F96] border-l border-[#D8D9E8]">
                          {num(c.registrations)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(c.students)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(c.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331] font-medium">
                          {usd(c.collected_usd)}
                          <span className="ml-1 text-[10px] text-[#5C635F]">{pct(c.collected_usd, c.amount_usd)}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(c.balance_usd)}</TableCell>
                      </TableRow>
                    ))}
                    {classRows.length > 0 && (
                      <TableRow className="bg-[#ECEDF5] font-bold border-t-2 border-[#3E3F96]"
                        data-testid="reg-class-total-row">
                        <TableCell colSpan={classPerBranch ? 2 : 1} className="uppercase text-xs tracking-wide">
                          Total count
                        </TableCell>
                        {(data.by_fee_head || []).map((h) => (
                          <TableCell key={h.fee_head} className="text-right tabular-nums">
                            {num(classRows.reduce((s, c) => s + (c.heads?.[h.fee_head]?.students || 0), 0))}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums text-[#3E3F96] border-l border-[#D8D9E8]">
                          {num(totals?.students)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(totals?.distinct_students)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals?.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331]">
                          {usd(totals?.collected_usd)}
                          <span className="ml-1 text-[10px] font-normal text-[#5C635F]">
                            {pct(totals?.collected_usd, totals?.amount_usd)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(totals?.balance_usd)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {classRows.length > 0 && (
                <p className="px-4 py-3 text-xs text-[#5C635F] border-t border-[#D8D9E8]">
                  Class is the student's <strong>current</strong> class — the system stores only that,
                  not the class they sat in when an older term was billed. So a 25-26 row is grouped
                  by where the student is today. Classes are in teaching order. As with branch, the
                  fee-head columns count a student once per class, so they do not add across to
                  Registrations.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ---- FY x Term x Fee Head ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Registrations by FY, Term and Fee Head
                <span className="ml-2 text-sm font-normal text-[#5C635F]">
                  {data.meta?.branches?.length ? data.meta.branches.join(', ') : 'All branches'}
                </span>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm"
                  onClick={() => downloadBlob('registrations_summary.csv',
                    toCsv(rows, [
                      { label: 'FY', get: (r) => r.fy_label },
                      { label: 'Academic Year', get: (r) => r.academic_year },
                      { label: 'Term', get: (r) => r.term },
                      { label: 'Fee Head', get: (r) => r.fee_head_label },
                      ...branchCols.map((b) => ({ label: b, get: (r) => cell(r, b) })),
                      { label: 'Students', get: (r) => r.students },
                      { label: 'Invoice Lines', get: (r) => r.line_items },
                      { label: 'Billed (USD)', get: (r) => r.amount_usd },
                      { label: 'Collected (USD)', get: (r) => r.collected_usd },
                      { label: 'Balance (USD)', get: (r) => r.balance_usd },
                    ]), 'text/csv;charset=utf-8;')}
                  data-testid="reg-csv">
                  <Download className="w-4 h-4 mr-1" />CSV
                </Button>
                {canExport && (
                  <Button variant="outline" size="sm" onClick={exportExcel} disabled={exporting}
                    data-testid="reg-excel">
                    {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                    Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">FY</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Term</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Fee Head</TableHead>
                      {branchCols.map((b) => (
                        <TableHead key={b}
                          className="text-xs uppercase font-semibold text-[#5C635F] text-right whitespace-nowrap">
                          {b}
                        </TableHead>
                      ))}
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right border-l border-[#D8D9E8]">Students</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Invoice Lines</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Billed (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Collected (USD)</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Balance (USD)</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9 + branchCols.length} className="text-center text-[#5C635F] py-8">
                          No billing found for the selected fee heads in this selection.
                        </TableCell>
                      </TableRow>
                    ) : rows.map((r) => (
                      <TableRow
                        key={`${r.academic_year}-${r.term}-${r.fee_head}`}
                        className="hover:bg-[#F5F5FA]"
                        data-testid={`reg-row-${r.academic_year}-${r.term}-${r.fee_head}`}
                      >
                        <TableCell className="font-medium">{r.fy_label}</TableCell>
                        <TableCell>{r.term || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{r.fee_head_label}</Badge>
                        </TableCell>
                        {/* Each branch cell drills into that branch's students only. */}
                        {branchCols.map((b) => (
                          <TableCell key={b} className="text-right tabular-nums p-0">
                            {cell(r, b) ? (
                              <button type="button"
                                className="w-full h-full px-4 py-2 text-right hover:underline text-[#3E3F96]"
                                onClick={() => openDrill({ ...r, branch: b })}
                                data-testid={`reg-cell-${r.academic_year}-${r.term}-${r.fee_head}-${b}`}
                              >
                                {num(cell(r, b))}
                              </button>
                            ) : (
                              <span className="block px-4 py-2 text-[#B7BAC6]">0</span>
                            )}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums p-0 border-l border-[#D8D9E8]">
                          <button type="button"
                            className="w-full h-full px-4 py-2 text-right font-bold text-[#3E3F96] hover:underline"
                            onClick={() => openDrill(r)}
                            data-testid={`reg-total-${r.academic_year}-${r.term}-${r.fee_head}`}
                          >
                            {num(r.students)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#5C635F]">{num(r.line_items)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(r.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331] font-medium">
                          {usd(r.collected_usd)}
                          <span className="ml-1 text-[10px] text-[#5C635F]">{pct(r.collected_usd, r.amount_usd)}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(r.balance_usd)}</TableCell>
                        <TableCell><ChevronRight className="w-4 h-4 text-[#3E3F96]" /></TableCell>
                      </TableRow>
                    ))}
                    {rows.length > 0 && (
                      <TableRow className="bg-[#ECEDF5] font-bold border-t-2 border-[#3E3F96]"
                        data-testid="reg-total-row">
                        <TableCell colSpan={3} className="uppercase text-xs tracking-wide">
                          Total count
                        </TableCell>
                        {branchCols.map((b) => (
                          <TableCell key={b} className="text-right tabular-nums"
                            data-testid={`reg-total-branch-${b}`}>
                            {num(totals?.by_branch?.[b]?.students)}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums text-[#3E3F96] border-l border-[#D8D9E8]">
                          {num(totals?.students)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(totals?.line_items)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals?.amount_usd)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#1A4331]">
                          {usd(totals?.collected_usd)}
                          <span className="ml-1 text-[10px] font-normal text-[#5C635F]">
                            {pct(totals?.collected_usd, totals?.amount_usd)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(totals?.balance_usd)}</TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {rows.length > 0 && (
                <p className="px-4 py-3 text-xs text-[#5C635F] border-t border-[#D8D9E8]">
                  The total adds the Students column up, so a student registered in more than one
                  term or head is counted once per row — that is the registration count.
                  Unique students across the whole selection: <strong>{num(totals?.distinct_students)}</strong>.
                  Click any row to see who is in it.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ---- Drill-down: who is counted ---- */}
      <Dialog open={!!drill} onOpenChange={(open) => { if (!open) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {drill?.fee_head_label} registrations — {drill?.fy_label} {drill?.term}
              {drill?.branch ? ` · ${drill.branch}` : ''}
            </DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
            </div>
          ) : drillData ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="relative flex-1 min-w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                  <Input placeholder="Search student, ID, class, invoice..." value={drillSearch}
                    onChange={(e) => setDrillSearch(e.target.value)} className="pl-9"
                    data-testid="reg-drill-search" />
                </div>
                <span className="text-sm text-[#5C635F]">
                  {num(filteredDrillRows.length)} of {num(drillData.count)} students
                </span>
                <Button variant="outline" size="sm"
                  onClick={() => downloadBlob(
                    `registrations_${drill?.fee_head}_${drill?.academic_year}_${(drill?.term || 'all').replace(/\s/g, '')}.csv`,
                    toCsv(filteredDrillRows, STUDENT_COLUMNS), 'text/csv;charset=utf-8;')}
                  data-testid="reg-drill-csv">
                  <Download className="w-4 h-4 mr-1" />CSV
                </Button>
              </div>
              {drillData.truncated && (
                <p className="text-xs text-[#C85A40]">
                  Showing the first {num(drillData.rows.length)} rows — narrow the filters or use
                  the Excel export for the full list.
                </p>
              )}
              <div className="overflow-x-auto max-h-[55vh]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      {STUDENT_COLUMNS.map((c) => (
                        <TableHead key={c.label}
                          className={`text-xs uppercase font-semibold text-[#5C635F] whitespace-nowrap ${
                            c.align === 'right' ? 'text-right' : ''}`}>
                          {c.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDrillRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={STUDENT_COLUMNS.length} className="text-center text-[#5C635F] py-8">
                          No students match this search.
                        </TableCell>
                      </TableRow>
                    ) : filteredDrillRows.map((r, i) => (
                      <TableRow key={`${r.student_id}-${r.fee_head}-${r.term}-${i}`}>
                        {STUDENT_COLUMNS.map((c) => (
                          <TableCell key={c.label}
                            className={`text-sm ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                            {c.get(r) || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationsReport;
